import { METADATA_FILTER_MATCH_TYPE } from "@/constants/filter-types"
import { buildSimulatedConversationsListPath } from "@/runs/table"
import { buildUploadedConversationsListPath } from "@/uploaded-conversations"

import type { MetadataFilter } from "@/types/MetadataFilter"

import { GROUP_BY_TYPE, type GroupByType } from "../types/chart-types"
import {
  DATA_SOURCE,
  METRIC_OUTPUT_TYPE,
  type DataSource,
  type FilterType,
  type MetricFilter,
  type MetricOutputType,
} from "../types/core"
import { parseMetadataGroupKey } from "./chart/splitKeys"
import type { TableRowData } from "./tableWidgetData"

interface TableCellConversationsPathInput {
  dataSource: DataSource
  dateRange: { start: Date; end: Date }
  filters?: FilterType
  groupBy?: GroupByType
  groupByMetadataKey?: string
  metricFilter?: MetricFilter[]
  metricId: string
  metricLabel: string
  metricOutputType: MetricOutputType
  row: TableRowData
}

const hasAny = (values: readonly unknown[] | undefined) =>
  Boolean(values?.length)

/**
 * The row's metadata group as a list filter, or null when the group has no
 * equivalent list filter (missing values and the remainder of the top values).
 */
const resolveMetadataGroupFilter = (
  groupByMetadataKey: string,
  row: TableRowData,
): MetadataFilter | null => {
  const group = row.metadataValue
    ? parseMetadataGroupKey(row.metadataValue)
    : null
  if (group?.kind !== "value" || group.value == null) {
    return null
  }

  return {
    key: groupByMetadataKey,
    value: group.value,
    matchType: METADATA_FILTER_MATCH_TYPE.EQUALS,
  }
}

const getRowId = (row: TableRowData, key: keyof TableRowData) => {
  const value = row[key]
  return typeof value === "string" && value.length > 0 ? value : null
}

const buildUploadedPath = (
  input: TableCellConversationsPathInput,
  metadataFilters: MetadataFilter[],
): string | null => {
  const { dateRange, filters, groupBy, row } = input
  if (
    hasAny(filters?.agentMutationIds) ||
    hasAny(filters?.personaIds) ||
    hasAny(filters?.templateNames) ||
    hasAny(filters?.testSetIds) ||
    hasAny(filters?.testCaseIds)
  ) {
    return null
  }

  let agentIds = filters?.agentIds ?? []
  if (groupBy) {
    const agentId =
      groupBy === GROUP_BY_TYPE.AGENT ? getRowId(row, "run_agent_id") : null
    if (!agentId) {
      return null
    }
    agentIds = [agentId]
  }

  return buildUploadedConversationsListPath({
    dateRange,
    metricValueFilters: [
      {
        metricId: input.metricId,
        metricDisplayName: input.metricLabel,
        operator: "exists",
        value: "",
        valueType: input.metricOutputType,
      },
    ],
    agentIds,
    tagFilters: filters?.tagFilters ?? [],
    metadataFilters,
  })
}

interface SimulatedListDimensionFilters {
  agentFilters: string[]
  mutationFilters: string[]
  personaFilters: string[]
  testSetFilters: string[]
  testCaseFilters: string[]
}

const SIMULATED_GROUP_BY_FILTER: Partial<
  Record<
    GroupByType,
    {
      rowKey: keyof TableRowData
      filterKey: keyof SimulatedListDimensionFilters
    }
  >
> = {
  [GROUP_BY_TYPE.AGENT]: { rowKey: "run_agent_id", filterKey: "agentFilters" },
  [GROUP_BY_TYPE.MUTATION]: {
    rowKey: "mutation_id",
    filterKey: "mutationFilters",
  },
  [GROUP_BY_TYPE.PERSONA]: {
    rowKey: "persona_id",
    filterKey: "personaFilters",
  },
  [GROUP_BY_TYPE.TEST_SET]: {
    rowKey: "test_set_id",
    filterKey: "testSetFilters",
  },
}

const buildSimulatedPath = (
  input: TableCellConversationsPathInput,
  metadataFilters: MetadataFilter[],
): string | null => {
  const { dateRange, filters, groupBy, row } = input
  if (hasAny(filters?.templateNames)) {
    return null
  }

  const listFilters: SimulatedListDimensionFilters = {
    agentFilters: filters?.agentIds ?? [],
    mutationFilters: filters?.agentMutationIds ?? [],
    personaFilters: filters?.personaIds ?? [],
    testSetFilters: filters?.testSetIds ?? [],
    testCaseFilters: filters?.testCaseIds ?? [],
  }

  if (groupBy) {
    const groupFilter = SIMULATED_GROUP_BY_FILTER[groupBy]
    const rowId = groupFilter ? getRowId(row, groupFilter.rowKey) : null
    if (!groupFilter || !rowId) {
      return null
    }
    listFilters[groupFilter.filterKey] = [rowId]
  }

  return buildSimulatedConversationsListPath({
    dateRange,
    metricValueFilters: [
      {
        metricName: input.metricId,
        operator: "exists",
        value: null,
        valueType:
          input.metricOutputType === METRIC_OUTPUT_TYPE.STRING
            ? "string"
            : "number",
      },
    ],
    tagFilters: filters?.tagFilters ?? [],
    metadataFilters,
    ...listFilters,
  })
}

/**
 * Path to the conversations list showing the conversations behind one table
 * cell, or null when the cell's scope cannot be expressed as list filters.
 */
export const buildTableCellConversationsPath = (
  input: TableCellConversationsPathInput,
): string | null => {
  if (
    input.row.metricCells[input.metricId]?.value == null ||
    hasAny(input.metricFilter)
  ) {
    return null
  }

  const metadataFilters: MetadataFilter[] = (input.filters?.metadata ?? []).map(
    ({ key, value }) => ({
      key,
      value,
      matchType: METADATA_FILTER_MATCH_TYPE.EQUALS,
    }),
  )
  if (input.groupByMetadataKey) {
    const groupFilter = resolveMetadataGroupFilter(
      input.groupByMetadataKey,
      input.row,
    )
    if (!groupFilter) {
      return null
    }
    metadataFilters.push(groupFilter)
  }

  switch (input.dataSource) {
    case DATA_SOURCE.UPLOADED:
      return buildUploadedPath(input, metadataFilters)
    case DATA_SOURCE.SIMULATED:
      return buildSimulatedPath(input, metadataFilters)
    case DATA_SOURCE.HUMAN_REVIEW:
      return null
  }
}
